import {
  consumeDisposablePublicationEvidence,
  type DisposableFormulaPublicationEvidence,
} from '../formula-studio-ui/formulaStudioPreview';
import { createQuoteFieldPool } from '../mock/productCatalog';
import {
  approvedScenarioForRole,
  approvedScopesForAction,
  createInitialPermissionGovernance,
} from '../permission-center-ui/DisposablePermissionPreview.fixture';
import {
  buildPermissionBusinessFieldRows,
  isQuoteDefinitionBound,
  QUOTE_SOURCE_FIELDS,
  quoteDefinitionBindingForKey,
} from './businessFieldDirectory';
import { customerRecordForId, quoteCustomerForRecord } from './customerBinding';
import {
  assertCustomFieldDraft,
  isValidCustomFieldDefinition,
  ownerForCustomDefinition,
} from './customFieldValues';
import { hydrateDeviceCatalogRecord } from './deviceCatalog';
import {
  assertMockStorageCapacity,
  normalizeReplacementIds,
} from './deviceMedia';
import { hasDiscountPercentPrecision } from './discountPrecision';
import {
  assertValidCalculationDefinition,
  hasCalculationCycle,
} from './fieldCalculations';
import {
  activeActorPermissionContext,
  activeRoleIdForState,
  canEditQuoteSourceField,
  canUseActivePermissionAction,
  canViewQuoteSourceField,
  channelRequiredFieldGrant,
  resolveDisposableFieldGrant,
} from './permissionPolicy';
import { assertCatalogDeviceWriteAllowed } from './quoteFieldPolicy';
import { shareFieldCombinationFieldsOutsidePool } from './shareExportFieldCombinations';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';
import {
  createSystemFieldRegistry,
  createUnifiedFieldRegistry,
  FIXED_QUOTE_HEADER_FIELD_KEYS,
  unifiedFieldBindingForKey,
} from './unifiedFieldRegistry';

export {
  resolveDisposableChannelFieldIds,
  resolveDisposablePermissionAction,
} from './permissionPolicy';

import type {
  CpqRole,
  CpqState,
  CustomerCatalogRecord,
  DeviceCatalogRecord,
  DisposableRolePermissionPolicy,
  PackageCatalogRecord,
  PlanCatalogRecord,
  QuoteFieldCombinationRecord,
  QuoteFieldDefinitionRecord,
  QuoteFieldPoolRecord,
  QuoteFieldPresetRecord,
  RoleCatalogRecord,
  UserCatalogRecord,
} from './types';

const MOCK_TIME = '2026-08-12T12:00:00.000Z';

export function isSafePreviewFormulaVersion(value: unknown): value is string {
  if (typeof value !== 'string' || !/^preview-v[1-9]\d*$/.test(value))
    return false;
  return Number.isSafeInteger(Number(value.slice('preview-v'.length)));
}

export type DeviceCatalogWriteInput = Omit<
  DeviceCatalogRecord,
  'updatedAt' | 'updatedAtSource'
> &
  Partial<Pick<DeviceCatalogRecord, 'updatedAt' | 'updatedAtSource'>>;

function clone(state: CpqState): CpqState {
  return structuredClone(state);
}

function assertBusinessFieldEdit(state: CpqState, fieldId: string): void {
  const roleId = activeRoleIdForState(state);
  if (
    !roleId ||
    !resolveDisposableFieldGrant(
      state,
      roleId,
      fieldId,
      'edit',
      'draft',
      activeActorPermissionContext(state),
    )
  )
    throw new Error('字段编辑权限不足，未保存业务事实');
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value))
    return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function deviceBusinessSnapshot(input: DeviceCatalogWriteInput): string {
  const {
    stockByWarehouse: _stockByWarehouse,
    maintenance: _maintenance,
    updatedAt: _updatedAt,
    updatedAtSource: _updatedAtSource,
    ...business
  } = input;
  return canonicalJson(business);
}

function isCanonicalIsoTime(value: string) {
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
}

export function saveDeviceCatalog(
  state: CpqState,
  input: DeviceCatalogWriteInput,
  updatedAt: string | undefined,
  fieldValuePort: UnifiedFieldValuePort,
): CpqState {
  updatedAt ??= new Date().toISOString();
  if (
    !canUseActivePermissionAction(state, 'devices', 'devices:catalog.manage')
  ) {
    throw new Error('当前角色无设备维护权限');
  }
  const currentDevice = state.catalog.devices.find(
    (device) => device.id === input.id,
  );
  assertCustomFieldDraft(
    state,
    'device',
    currentDevice?.customFieldValues,
    input.customFieldValues,
  );
  const name = input.name.trim();
  const sku = input.sku?.trim() || null;
  if (!name) throw new Error('请输入设备名称');
  if (!sku && input.status === 'active')
    throw new Error('启用设备 SKU 待维护，可停用保存');
  if (
    input.weight != null &&
    (!Number.isFinite(input.weight) || input.weight < 0)
  ) {
    throw new Error('设备重量必须是大于等于 0 的有限 kg 数字');
  }
  if (
    [
      input.rentalPrice,
      input.retailPriceTax13,
      input.unitPrice,
      input.untaxedPlanPrice,
      input.dynamicCost,
      input.compensationPrice,
    ].some((value) => value != null && (!Number.isFinite(value) || value < 0))
  )
    throw new Error('设备人工价格必须是非负有限数字');
  if (
    [
      input.taxInclusivePlanPrice,
      input.buyoutFinalPriceTax13,
      input.purchaseFinalPriceExTax13,
    ].some(
      (value) =>
        value != null &&
        !Number.isNaN(value) &&
        (!Number.isFinite(value) || value < 0),
    )
  )
    throw new Error('最终买断价和购买最终价必须是非负有限数字或待维护');
  if (
    sku &&
    state.catalog.devices.some(
      (device) =>
        device.id !== input.id &&
        device.sku?.toLowerCase() === sku.toLowerCase(),
    )
  ) {
    throw new Error(`SKU ${sku} 已存在`);
  }
  const replacementIds = normalizeReplacementIds(
    input.replacementIds,
    input.id,
    new Set(state.catalog.devices.map((device) => device.id)),
  );
  const normalizedInput = hydrateDeviceCatalogRecord({
    ...input,
    replacementIds,
  } as DeviceCatalogRecord);
  let next = clone(state);
  const existing = next.catalog.devices.find(
    (device) => device.id === input.id,
  );
  const businessChanged =
    !existing ||
    deviceBusinessSnapshot(hydrateDeviceCatalogRecord(existing)) !==
      deviceBusinessSnapshot(normalizedInput);
  if (businessChanged && !isCanonicalIsoTime(updatedAt)) {
    throw new Error('设备更新时间必须是 ISO 8601 时间');
  }
  const saved: DeviceCatalogRecord = {
    ...structuredClone(normalizedInput),
    customFieldValues: undefined,
    name,
    sku,
    updatedAt: businessChanged ? updatedAt : existing.updatedAt,
    updatedAtSource: businessChanged ? 'user-save' : existing.updatedAtSource,
  };
  assertCatalogDeviceWriteAllowed(state, existing, saved);
  const index = next.catalog.devices.findIndex(
    (device) => device.id === saved.id,
  );
  if (index >= 0) next.catalog.devices[index] = saved;
  else next.catalog.devices.push(saved);
  next = fieldValuePort.replace(
    next,
    'device',
    saved.id,
    input.customFieldValues,
  );
  assertMockStorageCapacity(next);
  return next;
}

export function savePackageCatalog(
  state: CpqState,
  input: PackageCatalogRecord,
  fieldValuePort: UnifiedFieldValuePort,
): CpqState {
  if (
    !canUseActivePermissionAction(state, 'packages', 'packages:solution.manage')
  )
    throw new Error('当前成员无套餐维护权限');
  if ('originalPrice' in input || 'discountedQuote' in input) {
    throw new Error(
      '套餐单品价小计与套餐价由设备单品价和数量计算，不接受人工写入',
    );
  }
  const existingPackage = state.catalog.packages.find(
    (item) => item.id === input.id,
  );
  assertCustomFieldDraft(
    state,
    'package',
    existingPackage?.customFieldValues,
    input.customFieldValues,
  );
  for (const property of [
    'name',
    'planIds',
    'sortOrder',
    'image',
    'lightImage',
  ] as const) {
    if (
      !existingPackage ||
      JSON.stringify(existingPackage[property]) !==
        JSON.stringify(input[property])
    ) {
      assertBusinessFieldEdit(state, `source:PackageCatalogRecord.${property}`);
    }
  }
  if (
    !existingPackage ||
    JSON.stringify(
      existingPackage.productLinks.map((link) => link.quantity),
    ) !== JSON.stringify(input.productLinks.map((link) => link.quantity))
  ) {
    assertBusinessFieldEdit(state, 'source:PackageProductLink.quantity');
  }
  if (
    !existingPackage ||
    JSON.stringify(
      existingPackage.productLinks.map((link) => link.packageItemPrice),
    ) !==
      JSON.stringify(input.productLinks.map((link) => link.packageItemPrice))
  ) {
    assertBusinessFieldEdit(state, 'definition:package_item_price');
  }
  const name = input.name.trim();
  if (!name) throw new Error('请输入套餐名称');
  const knownDeviceIds = new Set(
    state.catalog.devices.map((device) => device.id),
  );
  const knownPlanIds = new Set(state.catalog.plans.map((plan) => plan.id));
  const relationDeviceIds = input.productLinks.map((link) => link.deviceId);
  if (
    new Set(input.planIds).size !== input.planIds.length ||
    input.planIds.some((planId) => !knownPlanIds.has(planId)) ||
    new Set(input.productLinks.map((link) => link.relationId)).size !==
      input.productLinks.length ||
    new Set(relationDeviceIds).size !== relationDeviceIds.length ||
    input.productLinks.some(
      (link) =>
        link.packageId !== input.id ||
        !link.deviceId.trim() ||
        !knownDeviceIds.has(link.deviceId) ||
        !Number.isInteger(link.quantity) ||
        link.quantity < 1 ||
        !Number.isInteger(link.sortOrder) ||
        link.sortOrder < 0 ||
        (link.packageItemPrice !== null &&
          (!Number.isFinite(link.packageItemPrice) ||
            link.packageItemPrice < 0)),
    )
  )
    throw new Error('套餐设备与方案关联必须使用唯一稳定标识并绑定现有目录实体');
  let next = clone(state);
  const saved = {
    ...structuredClone(input),
    customFieldValues: undefined,
    name,
    productLinks: [...input.productLinks].sort(
      (left, right) => left.sortOrder - right.sortOrder,
    ),
  };
  const index = next.catalog.packages.findIndex((item) => item.id === saved.id);
  if (index >= 0) next.catalog.packages[index] = saved;
  else next.catalog.packages.push(saved);
  next = fieldValuePort.replace(
    next,
    'package',
    saved.id,
    input.customFieldValues,
  );
  return next;
}

export function saveCustomerCatalog(
  state: CpqState,
  input: CustomerCatalogRecord,
  fieldValuePort: UnifiedFieldValuePort,
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'customers',
      'customers:customer.manage',
    )
  )
    throw new Error('当前成员无客户维护权限');
  if (!input.id.trim()) throw new Error('客户标识不能为空');
  if (!input.name.trim()) throw new Error('请填写客户名称');
  if (!input.customerNo.trim()) throw new Error('请填写客户编号');
  if (!input.manager.trim() || !input.managerUserId) {
    throw new Error('客户必须绑定有效客户经理');
  }
  if (!['active', 'inactive'].includes(input.status)) {
    throw new Error('客户状态无效');
  }
  const manager = state.catalog.users.find(
    (user) => user.id === input.managerUserId && user.status === 'active',
  );
  if (!manager) throw new Error('客户经理不存在或已停用');
  const existingCustomer = state.catalog.customers.find(
    (item) => item.id === input.id,
  );
  assertCustomFieldDraft(
    state,
    'customer',
    existingCustomer?.customFieldValues,
    input.customFieldValues,
  );
  const customerFields: Array<[keyof CustomerCatalogRecord, string]> = [
    ['customerNo', 'source:CustomerCatalogRecord.customerNo'],
    ['name', 'source:CustomerCatalogRecord.name'],
    ['contact', 'source:CustomerCatalogRecord.contact'],
    ['phone', 'source:CustomerCatalogRecord.phone'],
    ['manager', 'source:CustomerCatalogRecord.manager'],
    ['status', 'source:CustomerCatalogRecord.status'],
  ];
  for (const [property, fieldId] of customerFields) {
    if (
      !existingCustomer ||
      JSON.stringify(existingCustomer[property]) !==
        JSON.stringify(input[property])
    ) {
      assertBusinessFieldEdit(state, fieldId);
    }
  }
  if (
    state.catalog.customers.some(
      (customer) =>
        customer.id !== input.id &&
        customer.customerNo.trim() === input.customerNo.trim(),
    )
  ) {
    throw new Error(`客户编号 ${input.customerNo.trim()} 已存在`);
  }
  let next = clone(state);
  const saved = {
    ...structuredClone(input),
    customFieldValues: undefined,
    name: input.name.trim(),
    customerNo: input.customerNo.trim(),
  };
  const index = next.catalog.customers.findIndex(
    (item) => item.id === saved.id,
  );
  if (index >= 0) next.catalog.customers[index] = saved;
  else next.catalog.customers.push(saved);
  next = fieldValuePort.replace(
    next,
    'customer',
    saved.id,
    input.customFieldValues,
  );
  return next;
}

function customerBindingFieldsAllowed(state: CpqState, quoteId: string): void {
  const quote = state.quotes[quoteId];
  if (!quote) throw new Error(`报价 ${quoteId} 不存在`);
  if (quote.status !== 'draft') throw new Error('非草稿报价只读，不能绑定客户');
  const activeRoleId = activeRoleIdForState(state);
  if (
    !activeRoleId ||
    !canUseActivePermissionAction(state, 'quotes', 'quotes:quote.edit')
  ) {
    throw new Error('报价编辑权限不足，未保存客户绑定');
  }
  const customerFieldIds = [
    QUOTE_SOURCE_FIELDS.customerSnapshot,
    QUOTE_SOURCE_FIELDS.customerName,
    QUOTE_SOURCE_FIELDS.customerPhone,
  ];
  if (
    customerFieldIds.some(
      (fieldId) =>
        !canViewQuoteSourceField(state, quote, fieldId) ||
        !canEditQuoteSourceField(state, quote, fieldId),
    )
  ) {
    throw new Error('客户字段查看或编辑上限不足');
  }
}

/** Binds a draft quote to an exact customer-directory identity. */
export function bindQuoteCustomer(
  state: CpqState,
  quoteId: string,
  customerId: string,
): CpqState {
  const quote = state.quotes[quoteId];
  if (!quote) throw new Error(`报价 ${quoteId} 不存在`);
  const customer = customerRecordForId(state, customerId);
  if (
    !canUseActivePermissionAction(state, 'customers', 'customers:customer.read')
  )
    throw new Error('当前成员无权读取客户目录，未保存客户绑定');
  if (quote.status !== 'draft') {
    if (
      quote.customer.customerId === customer.id &&
      JSON.stringify(quote.customer) ===
        JSON.stringify(quoteCustomerForRecord(customer))
    ) {
      return state;
    }
    throw new Error('非草稿报价只读，不能更换客户绑定');
  }
  customerBindingFieldsAllowed(state, quoteId);
  const next = clone(state);
  const target = next.quotes[quoteId];
  if (!target) throw new Error(`报价 ${quoteId} 不存在`);
  target.customer = quoteCustomerForRecord(customer);
  target.dirty = true;
  target.updatedAt = new Date().toISOString();
  return next;
}

export interface NewCustomerBindingInput {
  name: string;
  contact: string;
  phone: string;
}

/**
 * Explicitly creates one directory record and binds it to a draft quote. It
 * never searches by name/phone to select an existing record; exact collisions
 * fail closed and require the user to choose an existing directory option.
 */
export function createAndBindCustomerForQuote(
  state: CpqState,
  quoteId: string,
  input: NewCustomerBindingInput,
  fieldValuePort: UnifiedFieldValuePort,
): CpqState {
  const quote = state.quotes[quoteId];
  if (!quote) throw new Error(`报价 ${quoteId} 不存在`);
  if (quote.status !== 'draft') {
    throw new Error('非草稿报价未绑定客户，不能在发布时新建客户');
  }
  if (
    !canUseActivePermissionAction(
      state,
      'customers',
      'customers:customer.manage',
    )
  ) {
    throw new Error('当前成员无客户维护权限，未创建客户');
  }
  const name = input.name.trim();
  const contact = input.contact.trim();
  const phone = input.phone.trim();
  if (!name) throw new Error('请输入客户名称');
  if (!/^1\d{10}$/.test(phone)) throw new Error('请输入正确的联系电话');
  const duplicate = state.catalog.customers.find(
    (customer) =>
      customer.name.trim() === name || customer.phone.trim() === phone,
  );
  if (duplicate) {
    throw new Error(
      '客户名称或联系电话已存在，请从客户目录明确选择已有客户；未自动匹配或重复创建',
    );
  }
  const actor = state.catalog.users.find(
    (user) => user.id === state.currentActorUserId && user.status === 'active',
  );
  if (!actor) throw new Error('当前演示身份无效，未创建客户');
  let sequence = Math.max(1, state.nextSequence);
  const existingIds = new Set(
    state.catalog.customers.map((customer) => customer.id),
  );
  const existingNumbers = new Set(
    state.catalog.customers.map((customer) => customer.customerNo),
  );
  let id = `CUSTOMER-${String(sequence).padStart(4, '0')}`;
  let customerNo = `CUST-${String(1000 + sequence)}`;
  while (existingIds.has(id) || existingNumbers.has(customerNo)) {
    sequence += 1;
    id = `CUSTOMER-${String(sequence).padStart(4, '0')}`;
    customerNo = `CUST-${String(1000 + sequence)}`;
  }
  const next = saveCustomerCatalog(
    state,
    {
      id,
      customerNo,
      name,
      contact,
      phone,
      manager: actor.displayName,
      managerUserId: actor.id,
      status: 'active',
    },
    fieldValuePort,
  );
  next.nextSequence = Math.max(next.nextSequence, sequence + 1);
  return bindQuoteCustomer(next, quoteId, id);
}

export function saveUserCatalog(
  state: CpqState,
  input: UserCatalogRecord,
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.user.manage',
    )
  )
    throw new Error('仅超级管理员可管理用户');
  if (!input.username.trim()) throw new Error('请填写用户名');
  if (!input.password) throw new Error('请填写密码');
  const next = clone(state);
  const saved = structuredClone(input);
  const legacyMatches = next.catalog.roles.filter(
    (role) => role.key === saved.role,
  );
  const roleId =
    saved.roleId ??
    (legacyMatches.length === 1 ? legacyMatches[0].id : undefined);
  const role = roleId
    ? next.catalog.roles.find((item) => item.id === roleId)
    : undefined;
  if (!role) {
    throw new Error('请选择有效角色');
  }
  const current = state.catalog.users.find((item) => item.id === saved.id);
  if (current?.role === 'super_admin' || role.key === 'super_admin') {
    throw new Error('超级管理员账号为系统保护账号，不可新增、改绑或修改');
  }
  if (saved.roleId && saved.role !== role.key) {
    throw new Error('角色模板与稳定角色标识不一致');
  }
  saved.roleId = role.id;
  // `role` remains a legacy template compatibility value. The stable binding is roleId.
  saved.role = role.key;
  const index = next.catalog.users.findIndex((item) => item.id === saved.id);
  if (index >= 0) next.catalog.users[index] = saved;
  else next.catalog.users.push(saved);
  return next;
}

export function saveRoleCatalog(
  state: CpqState,
  input: RoleCatalogRecord,
  mode: 'create' | 'update' = 'update',
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('仅超级管理员可管理角色');
  if (!input.label.trim()) throw new Error('请填写角色名称');
  if (!input.id.trim()) throw new Error('角色标识不能为空');
  const next = clone(state);
  const saved = structuredClone(input);
  const index = next.catalog.roles.findIndex((item) => item.id === saved.id);
  if (saved.key === 'super_admin' || saved.id === 'ROLE-SUPER-ADMIN') {
    throw new Error('超级管理员角色为系统保护角色，不可修改');
  }
  if (mode === 'create') {
    if (index >= 0) throw new Error('角色标识已存在');
    next.catalog.roles.push(saved);
    next.catalog.permissionGovernance.roles[saved.id] =
      createInitialPermissionGovernance(
        [saved],
        next.catalog.quoteFieldDefinitions,
      ).roles[saved.id];
    return next;
  }
  if (index < 0) throw new Error('待更新角色不存在');
  if (next.catalog.roles[index].key !== saved.key) {
    throw new Error('角色模板不可变');
  }
  next.catalog.roles[index] = saved;
  return next;
}

/** Disposable catalogue seam: member identity follows RoleCatalogRecord.id, never a label. */
export function bindUserRoleCatalog(
  state: CpqState,
  userId: string,
  roleId: string,
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.user.manage',
    )
  )
    throw new Error('仅超级管理员可调整用户角色');
  const role = state.catalog.roles.find((item) => item.id === roleId);
  const user = state.catalog.users.find((item) => item.id === userId);
  if (!role) throw new Error('请选择有效角色');
  if (!user) throw new Error('用户不存在');
  if (user.role === 'super_admin' || role.key === 'super_admin') {
    throw new Error('超级管理员账号与角色不可改绑');
  }
  const next = clone(state);
  const target = next.catalog.users.find((item) => item.id === userId);
  if (!target) throw new Error('用户不存在');
  target.roleId = role.id;
  target.role = role.key;
  return next;
}

export function savePlanCatalog(
  state: CpqState,
  input: PlanCatalogRecord,
  fieldValuePort: UnifiedFieldValuePort,
): CpqState {
  if (!canUseActivePermissionAction(state, 'plans', 'plans:solution.manage'))
    throw new Error('当前成员无方案维护权限');
  if ('productPrices' in input || 'packagePrices' in input) {
    throw new Error(
      '方案专属产品价格与套餐价格由统一计算服务生成，不接受人工写入',
    );
  }
  const existingPlan = state.catalog.plans.find((item) => item.id === input.id);
  assertCustomFieldDraft(
    state,
    'plan',
    existingPlan?.customFieldValues,
    input.customFieldValues,
  );
  for (const property of [
    'name',
    'sortOrder',
    'image',
    'images',
    'cases',
  ] as const) {
    if (
      !existingPlan ||
      JSON.stringify(existingPlan[property]) !== JSON.stringify(input[property])
    ) {
      assertBusinessFieldEdit(
        state,
        `source:PlanCatalogRecord.${property === 'images' ? 'image' : property}`,
      );
    }
  }
  if (!existingPlan || existingPlan.discount !== input.discount) {
    assertBusinessFieldEdit(state, 'definition:scheme_discount');
    if (!hasDiscountPercentPrecision(input.discount))
      throw new Error('折扣最多保留两位小数');
  }
  if (!input.name.trim()) throw new Error('请填写方案名称');
  if (!Number.isFinite(input.discount) || input.discount < 0)
    throw new Error('折扣率必须是大于等于 0 的有限数值');
  let next = clone(state);
  const saved = {
    ...structuredClone(input),
    customFieldValues: undefined,
    name: input.name.trim(),
    images: [...new Set(input.images ?? (input.image ? [input.image] : []))],
  };
  saved.image = saved.images[0] ?? '';
  const index = next.catalog.plans.findIndex((item) => item.id === saved.id);
  if (index >= 0) next.catalog.plans[index] = saved;
  else next.catalog.plans.push(saved);
  next = fieldValuePort.replace(
    next,
    'plan',
    saved.id,
    input.customFieldValues,
  );
  return next;
}

export function savePlanPackageAssignments(
  state: CpqState,
  planId: string,
  packageIds: string[],
): CpqState {
  if (!canUseActivePermissionAction(state, 'plans', 'plans:solution.manage'))
    throw new Error('当前成员无方案维护权限');
  if (!state.catalog.plans.some((plan) => plan.id === planId))
    throw new Error('方案不存在');
  const selectedIds = new Set(packageIds);
  if (
    selectedIds.size !== packageIds.length ||
    packageIds.some(
      (packageId) =>
        !state.catalog.packages.some((pkg) => pkg.id === packageId),
    )
  )
    throw new Error('请选择有效且不重复的设备套餐');
  const currentPackageIds = state.catalog.packages
    .filter((pkg) => pkg.planIds.includes(planId))
    .map((pkg) => pkg.id);
  if (
    currentPackageIds.length !== packageIds.length ||
    currentPackageIds.some((packageId) => !selectedIds.has(packageId))
  ) {
    assertBusinessFieldEdit(state, 'source:PackageCatalogRecord.planIds');
  }
  const next = clone(state);
  next.catalog.packages = next.catalog.packages.map((pkg) => ({
    ...pkg,
    planIds: selectedIds.has(pkg.id)
      ? [...new Set([...pkg.planIds, planId])]
      : pkg.planIds.filter((id) => id !== planId),
  }));
  return next;
}

export function deletePlanCatalog(state: CpqState, planId: string): CpqState {
  if (!canUseActivePermissionAction(state, 'plans', 'plans:solution.manage'))
    throw new Error('当前成员无方案维护权限');
  if (!state.catalog.plans.some((plan) => plan.id === planId))
    throw new Error('方案不存在');
  if (state.catalog.packages.some((pkg) => pkg.planIds.includes(planId)))
    throw new Error('方案仍被套餐稳定关联，不能删除');
  const next = clone(state);
  next.catalog.plans = next.catalog.plans.filter((plan) => plan.id !== planId);
  return next;
}

export function saveWarehouseChanges(
  state: CpqState,
  deviceId: string,
  draft: Record<string, number>,
): { state: CpqState; changedWarehouseKeys: string[] } {
  if (
    !canUseActivePermissionAction(
      state,
      'inventory',
      'inventory:inventory.adjust',
    )
  )
    throw new Error('当前成员无库存调整权限');
  assertBusinessFieldEdit(state, 'definition:warehouse_stock');
  const source = state.catalog.devices.find((device) => device.id === deviceId);
  if (!source) throw new Error(`设备 ${deviceId} 不存在`);
  const knownKeys = new Set(
    state.catalog.warehouses.map((warehouse) => warehouse.key),
  );
  const changedWarehouseKeys = Object.entries(draft)
    .filter(([key, quantity]) => {
      if (!knownKeys.has(key)) throw new Error(`未知仓库 ${key}`);
      if (!Number.isInteger(quantity) || quantity < 0) {
        throw new Error('库存必须是大于等于 0 的整数');
      }
      return source.stockByWarehouse[key] !== quantity;
    })
    .map(([key]) => key);
  if (!changedWarehouseKeys.length) return { state, changedWarehouseKeys };

  const next = clone(state);
  const target = next.catalog.devices.find((device) => device.id === deviceId);
  if (!target) throw new Error(`设备 ${deviceId} 不存在`);
  for (const key of changedWarehouseKeys)
    target.stockByWarehouse[key] = draft[key];
  target.maintenance = {
    source: 'manual',
    updatedBy: '当前管理员',
    updatedAt: MOCK_TIME,
  };
  return { state: next, changedWarehouseKeys };
}

export function resyncWarehouseStock(state: CpqState): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'inventory',
      'inventory:inventory.adjust',
    )
  )
    throw new Error('当前成员无库存同步权限');
  assertBusinessFieldEdit(state, 'definition:warehouse_stock');
  const next = clone(state);
  next.catalog.devices = next.catalog.devices.map((device, deviceIndex) => ({
    ...device,
    stockByWarehouse: Object.fromEntries(
      next.catalog.warehouses.map((warehouse, warehouseIndex) => [
        warehouse.key,
        ((deviceIndex + 3) * (warehouseIndex + 5) * 7 + warehouseIndex * 11) %
          39,
      ]),
    ),
    maintenance: undefined,
  }));
  next.stockSyncRevision += 1;
  return next;
}

/** Disposable actor context, not a formal identity or generated API DTO. */
export interface QuoteFieldGovernanceActor {
  role: CpqRole;
  departmentId?: string;
}

function sameJson(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function assertPositiveInteger(value: number, message: string) {
  if (!Number.isInteger(value) || !Number.isFinite(value) || value < 1) {
    throw new Error(message);
  }
}

function isSafeDefinitionVersion(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^definition-v[1-9]\d*$/.test(value) &&
    Number.isSafeInteger(Number(value.slice('definition-v'.length)))
  );
}

function sameStableOptionValues(
  left: QuoteFieldDefinitionRecord['options'],
  right: QuoteFieldDefinitionRecord['options'],
) {
  return (
    JSON.stringify(left?.map((option) => option.value)) ===
    JSON.stringify(right?.map((option) => option.value))
  );
}

function hasOptionLabelChange(
  left: QuoteFieldDefinitionRecord['options'],
  right: QuoteFieldDefinitionRecord['options'],
) {
  return (
    optionValuesPreserved(left, right) &&
    optionValuesPreserved(right, left) &&
    JSON.stringify(left) !== JSON.stringify(right)
  );
}

function optionValuesPreserved(
  previous: QuoteFieldDefinitionRecord['options'],
  next: QuoteFieldDefinitionRecord['options'],
): boolean {
  const before = new Set((previous ?? []).map((option) => option.value));
  const after = new Set((next ?? []).map((option) => option.value));
  return [...before].every((value) => after.has(value));
}

function assertHistoricalOptionValuesPreserved(
  state: CpqState,
  current: QuoteFieldDefinitionRecord,
  next: QuoteFieldDefinitionRecord,
): void {
  if (current.type !== 'single-select' && current.type !== 'multi-select')
    return;
  const usedValues = new Set<string>();
  const collect = (value: unknown) => {
    if (typeof value === 'string') usedValues.add(value);
    if (Array.isArray(value))
      value.forEach((item) => {
        if (typeof item === 'string') usedValues.add(item);
      });
  };
  for (const record of [
    ...state.catalog.devices,
    ...state.catalog.packages,
    ...state.catalog.plans,
    ...state.catalog.customers,
  ])
    collect(record.customFieldValues?.[current.key]);
  for (const quote of Object.values(state.quotes)) {
    collect(quote.customFieldValues?.[current.key]);
    for (const item of quote.items)
      collect(item.manualFieldValues?.[current.key]);
    for (const snapshot of quote.customColumnSnapshot ?? []) {
      if (snapshot.key !== current.key) continue;
      for (const option of snapshot.options ?? []) usedValues.add(option.value);
    }
  }
  const nextValues = new Set(
    (next.options ?? []).map((option) => option.value),
  );
  for (const value of usedValues) {
    if (!nextValues.has(value))
      throw new Error('已有业务值或历史快照使用该选项，稳定值不可删除');
  }
}

function assertGovernedDefinition(
  state: CpqState,
  current: QuoteFieldDefinitionRecord | undefined,
  field: QuoteFieldDefinitionRecord,
) {
  if (
    !field.key.trim() ||
    !field.label.trim() ||
    !field.groupKey.trim() ||
    !field.groupLabel.trim()
  ) {
    throw new Error('字段名称、分组和 key 不能为空');
  }
  assertPositiveInteger(field.sortOrder, '字段排序必须是正整数');
  const governance = field.governance;
  if (!governance || governance.fieldKey !== field.key) {
    throw new Error('字段治理标识无效');
  }
  if (!isSafeDefinitionVersion(governance.definitionVersion)) {
    throw new Error('字段定义版本无效');
  }
  if (
    current &&
    governance.definitionVersion !== current.governance.definitionVersion
  ) {
    const labelRename = hasOptionLabelChange(field.options, current.options);
    const currentVersion = Number(
      (current.governance.definitionVersion ?? 'definition-v1').slice(
        'definition-v'.length,
      ),
    );
    const nextVersion = Number(
      governance.definitionVersion.slice('definition-v'.length),
    );
    if (
      !labelRename ||
      !Number.isSafeInteger(nextVersion) ||
      nextVersion <= currentVersion
    )
      throw new Error('字段定义版本不可伪造或直接覆盖');
  }
  if (current && field.groupKey !== current.groupKey) {
    throw new Error('已有字段归属不可变更');
  }
  const owner = ownerForCustomDefinition(field);
  if (
    field.quotePlacement !== undefined &&
    !['quote-line', 'package-summary'].includes(field.quotePlacement)
  ) {
    throw new Error('报价字段落点无效');
  }
  if (owner !== 'quote-line' && field.quotePlacement !== undefined) {
    throw new Error('仅报价行字段可配置报价显示层级');
  }
  if (
    current?.governance.kind === 'custom' &&
    current.quotePlacement !== field.quotePlacement
  ) {
    throw new Error('已有字段报价显示层级不可变更');
  }
  if (
    current &&
    JSON.stringify(current.calculation ?? null) !==
      JSON.stringify(field.calculation ?? null) &&
    field.governance.definitionVersion === current.governance.definitionVersion
  ) {
    throw new Error('修改计算定义必须生成新的字段定义版本');
  }
  if (
    current?.governance.kind === 'custom' &&
    (field.type !== current.type ||
      (!sameStableOptionValues(field.options, current.options) &&
        !hasOptionLabelChange(field.options, current.options)))
  ) {
    throw new Error(
      '企业字段类型与稳定选项不可直接变更；历史值和快照必须保持可解释',
    );
  }
  if (current?.governance.kind === 'custom')
    assertHistoricalOptionValuesPreserved(state, current, field);
  if (
    !['ordinary', 'sensitive', 'unclassified'].includes(
      governance.classification,
    )
  ) {
    throw new Error('字段分类无效');
  }
  if (field.sourceKind === 'system' && governance.kind === 'custom') {
    throw new Error('系统字段类型不可变');
  }
  if (field.sourceKind === 'manual' && governance.kind !== 'custom') {
    throw new Error('自定义字段类型不可变');
  }
  if (
    !current &&
    (!/^field\.custom\.new_[1-9]\d*$/.test(field.key) ||
      !ownerForCustomDefinition(field) ||
      ownerForCustomDefinition(field) === 'quote-header' ||
      field.sourceKind !== 'manual' ||
      governance.kind !== 'custom' ||
      !(
        (governance.classification === 'unclassified' &&
          !field.enabled &&
          !field.defaultVisible) ||
        (governance.classification === 'ordinary' &&
          field.enabled &&
          field.defaultVisible)
      ) ||
      governance.required ||
      field.systemLocked ||
      !governance.lockedAttributes.includes('fieldKey') ||
      !governance.lockedAttributes.includes('kind') ||
      governance.definitionVersion !== 'definition-v1')
  ) {
    throw new Error(
      '新增字段必须归属设备、套餐、方案或客户，并默认启用、默认显示',
    );
  }
  if (governance.kind === 'custom' && !isValidCustomFieldDefinition(field)) {
    throw new Error('企业自定义字段类型或选项无效');
  }
  if (
    (governance.classification === 'unclassified' && field.enabled) ||
    (!field.enabled && (governance.required || field.defaultVisible))
  ) {
    throw new Error('待分类或停用字段不能设为必填或默认显示');
  }
  if (current?.systemLocked) {
    if (
      field.key !== current.key ||
      field.sourceKind !== current.sourceKind ||
      field.systemLocked !== current.systemLocked ||
      field.type !== current.type ||
      field.width !== current.width ||
      !sameJson(
        field.governance.lockedAttributes,
        current.governance.lockedAttributes,
      )
    ) {
      throw new Error('系统字段锁定属性不可修改');
    }
    for (const attribute of current.governance.lockedAttributes) {
      if (
        (attribute === 'fieldKey' &&
          field.governance.fieldKey !== current.governance.fieldKey) ||
        (attribute === 'kind' &&
          field.governance.kind !== current.governance.kind) ||
        (attribute === 'classification' &&
          field.governance.classification !==
            current.governance.classification) ||
        (attribute === 'enabled' && field.enabled !== current.enabled) ||
        (attribute === 'required' &&
          field.governance.required !== current.governance.required) ||
        (attribute === 'defaultVisible' &&
          field.defaultVisible !== current.defaultVisible)
      )
        throw new Error('系统字段锁定属性不可修改');
    }
  }
}

export function saveQuoteFieldConfiguration(
  state: CpqState,
  definitions: QuoteFieldDefinitionRecord[],
  presets: QuoteFieldPresetRecord[],
  _legacyActor?: QuoteFieldGovernanceActor,
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  ) {
    throw new Error('当前角色无字段治理权限');
  }
  if (!definitions.length) throw new Error('至少保留一个报价字段');
  const keys = definitions.map((field) => field.key.trim());
  if (new Set(keys).size !== keys.length) throw new Error('字段 key 不能重复');
  const labels = definitions.map((field) => field.label.trim());
  if (new Set(labels).size !== labels.length)
    throw new Error('字段展示名不能重复，未保存本次字段治理');
  const currentByKey = new Map(
    state.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  const incomingKeys = new Set(keys);
  if (
    state.catalog.quoteFieldDefinitions.some(
      (field) => !incomingKeys.has(field.key),
    )
  ) {
    throw new Error('既有字段不可删除或改名');
  }
  definitions.forEach((field) => {
    assertGovernedDefinition(state, currentByKey.get(field.key), field);
  });
  const canonicalRegistry = createUnifiedFieldRegistry(definitions);
  const canonicalByKey = new Map(
    canonicalRegistry.map((descriptor) => [
      descriptor.definition.key,
      descriptor,
    ]),
  );
  const calculationFields = definitions.map((field) => ({
    key: field.key,
    label: field.label,
    owner: (() => {
      const descriptor = canonicalByKey.get(field.key);
      if (!descriptor) throw new Error(`统一字段计算归属缺失：${field.key}`);
      return descriptor.owner;
    })(),
    type: field.type,
    enabled: field.enabled,
    calculation: field.calculation,
    factorApplicability: canonicalByKey.get(field.key)?.binding
      .factorApplicability,
  }));
  for (const field of definitions) {
    if (!field.calculation) continue;
    assertValidCalculationDefinition(field.calculation, {
      fields: calculationFields,
      allowSystemCrossOwner: field.systemLocked,
      currentFieldKey: field.key,
    });
  }
  if (hasCalculationCycle(calculationFields))
    throw new Error('计算字段存在循环依赖');
  if (presets.filter((preset) => preset.isDefault).length !== 1) {
    throw new Error('必须且只能设置一个默认预设');
  }
  const knownKeys = new Set(keys);
  const usablePresetKeys = new Set(
    definitions
      .filter(
        (field) =>
          field.enabled && field.governance.classification === 'ordinary',
      )
      .map((field) => field.key),
  );
  // Definitions are the source of a pool entry.  Do not let a disable or a
  // classification change silently leave an enabled global combination with
  // an unusable member.  Validate before cloning so a rejected transition is
  // a strict zero-write.
  const nextSelectablePoolKeys = new Set(
    state.catalog.quoteFieldPool
      .filter((entry) => entry.enabled && entry.placement === 'quote-line')
      .map((entry) => entry.fieldKey)
      .filter((key) => usablePresetKeys.has(key)),
  );
  if (
    state.catalog.quoteFieldCombinations.some(
      (combination) =>
        combination.enabled &&
        combination.fieldKeys.some((key) => !nextSelectablePoolKeys.has(key)),
    )
  ) {
    throw new Error('字段定义变更将使已启用快捷组合越界，请先修改组合');
  }
  for (const preset of presets) {
    if (!preset.name.trim()) throw new Error('预设名称不能为空');
    assertPositiveInteger(preset.sortOrder, '预设排序必须是正整数');
    if (
      preset.fieldKeys.some(
        (key) => !knownKeys.has(key) || !usablePresetKeys.has(key),
      )
    ) {
      throw new Error('预设不能引用待分类、敏感、停用或未知字段');
    }
    if (
      preset.fieldOverrides &&
      Object.entries(preset.fieldOverrides).some(
        ([key, override]) =>
          !usablePresetKeys.has(key) ||
          !override.displayLabel.trim() ||
          !Number.isInteger(override.sortOrder) ||
          !Number.isFinite(override.sortOrder) ||
          override.sortOrder < 1,
      )
    )
      throw new Error('预设字段覆盖无效');
  }
  const next = clone(state);
  next.catalog.quoteFieldDefinitions = [...definitions]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((field, index) => ({
      ...structuredClone(field),
      key: field.key.trim(),
      label: field.label.trim(),
      sortOrder: index + 1,
      defaultVisible: field.enabled && field.defaultVisible,
    }));
  const knownFieldGrantIds = new Set(
    next.catalog.quoteFieldDefinitions.map(
      (field) => `definition:${field.key}`,
    ),
  );
  for (const policy of Object.values(next.catalog.permissionGovernance.roles)) {
    for (const fieldId of knownFieldGrantIds) {
      if (!policy.fieldGrants[fieldId]) {
        policy.fieldGrants[fieldId] = {
          view: false,
          edit: false,
          export: false,
        };
      }
    }
  }
  for (const role of next.catalog.roles) {
    if (role.key !== 'super_admin') continue;
    const policy = next.catalog.permissionGovernance.roles[role.id];
    if (!policy) continue;
    for (const field of next.catalog.quoteFieldDefinitions) {
      const previous = currentByKey.get(field.key);
      const newlyAvailable =
        !previous ||
        (previous.governance.classification === 'unclassified' &&
          field.governance.classification === 'ordinary') ||
        (!previous.enabled && field.enabled);
      if (!newlyAvailable) continue;
      const row = buildPermissionBusinessFieldRows(
        next.catalog.quoteFieldDefinitions,
      ).find((candidate) => candidate.id === `definition:${field.key}`);
      if (row?.available && row.editable) {
        policy.fieldGrants[`definition:${field.key}`] = {
          view: true,
          edit: true,
          export: true,
        };
      }
    }
  }
  // A pool label is independently editable.  Only an untouched display label
  // follows a renamed source definition; an explicit pool override survives.
  const priorDefinitions = new Map(
    state.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  next.catalog.quoteFieldPool = state.catalog.quoteFieldPool.map((entry) => {
    const previous = priorDefinitions.get(entry.fieldKey);
    const current = next.catalog.quoteFieldDefinitions.find(
      (field) => field.key === entry.fieldKey,
    );
    return previous && current && entry.displayLabel === previous.label
      ? { ...entry, displayLabel: current.label }
      : entry;
  });
  next.catalog.quoteFieldPresets = [...presets]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((preset, index) => ({
      ...structuredClone(preset),
      name: preset.name.trim() || `报价预设 ${index + 1}`,
      sortOrder: index + 1,
      fieldKeys: preset.fieldKeys.filter((key) => knownKeys.has(key)),
    }));
  return next;
}

const permissionReadDependencies: Record<string, string> = {
  'customer.manage': 'customer.read',
  'catalog.manage': 'catalog.read',
  'solution.manage': 'solution.read',
  'quote.edit': 'quote.read',
  'quote.finalize': 'quote.read',
  'quote.share.internal': 'quote.read',
  'quote.share.external': 'quote.read',
  'quote.submit': 'quote.read',
  'quote.export': 'quote.read',
  'inventory.adjust': 'inventory.read',
};

function disposableDepartmentId(label: string) {
  return `department:${encodeURIComponent(label.trim())}`;
}

/** Atomic Disposable-only role publication. No formal API/RBAC claim is made. */
export function publishDisposableRolePermissionPolicy(
  state: CpqState,
  roleId: string,
  draft: DisposableRolePermissionPolicy,
  expectedPublishedVersion: number,
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('仅超级管理员可发布权限');
  const role = state.catalog.roles.find((item) => item.id === roleId);
  const current = state.catalog.permissionGovernance.roles[roleId];
  if (role?.status !== 'active' || !current) throw new Error('角色不可用');
  if (role.key === 'super_admin') throw new Error('超级管理员策略受系统保护');
  if (
    draft.roleId !== roleId ||
    expectedPublishedVersion !== current.publishedVersion ||
    draft.publishedVersion !== current.publishedVersion
  )
    throw new Error('权限版本冲突');
  if (
    !Number.isSafeInteger(current.publishedVersion) ||
    current.publishedVersion < 1 ||
    current.publishedVersion >= Number.MAX_SAFE_INTEGER ||
    !Number.isSafeInteger(
      state.catalog.permissionGovernance.permissionVersion,
    ) ||
    state.catalog.permissionGovernance.permissionVersion < 1 ||
    state.catalog.permissionGovernance.permissionVersion >=
      Number.MAX_SAFE_INTEGER
  )
    throw new Error('权限版本已耗尽或无效');

  const approved = approvedScenarioForRole(role.key);
  const approvedFeatures = new Map(
    approved.features.map((item) => [item.id, item]),
  );
  const featureIds = Object.keys(draft.featureEnabled);
  if (
    featureIds.length !== approvedFeatures.size ||
    featureIds.some((id) => !approvedFeatures.has(id)) ||
    Object.values(draft.featureEnabled).some(
      (value) => typeof value !== 'boolean',
    )
  )
    throw new Error('功能草稿包含未知或缺失项');

  const approvedActions = new Map(
    approved.features.flatMap((feature) =>
      feature.actions.map(
        (action) => [action.id, { feature, action }] as const,
      ),
    ),
  );
  const actionIds = Object.keys(draft.actions);
  if (
    actionIds.length !== approvedActions.size ||
    actionIds.some((id) => !approvedActions.has(id))
  )
    throw new Error('动作草稿包含未知或缺失项');
  for (const [id, policy] of Object.entries(draft.actions)) {
    const approval = approvedActions.get(id);
    if (!approval) throw new Error('未知权限动作');
    if (
      typeof policy.enabled !== 'boolean' ||
      (policy.enabled &&
        (!approval.action.baseAllowed || approval.action.explicitDeny)) ||
      !approvedScopesForAction(approval.action.scope).includes(policy.scope) ||
      !Array.isArray(policy.specifiedDepartmentIds) ||
      new Set(policy.specifiedDepartmentIds).size !==
        policy.specifiedDepartmentIds.length
    )
      throw new Error('权限动作或数据范围越界');
    const knownDepartments = new Set(
      state.catalog.users.map((user) =>
        disposableDepartmentId(user.department),
      ),
    );
    if (
      (policy.scope === 'specified_departments' &&
        policy.specifiedDepartmentIds.length === 0) ||
      (policy.scope !== 'specified_departments' &&
        policy.specifiedDepartmentIds.length > 0) ||
      policy.specifiedDepartmentIds.some((id) => !knownDepartments.has(id))
    )
      throw new Error('指定部门范围无效');
  }
  for (const [id, policy] of Object.entries(draft.actions)) {
    const approval = approvedActions.get(id);
    const readPermission = approval
      ? permissionReadDependencies[approval.action.permission]
      : undefined;
    const readAction = readPermission
      ? approval?.feature.actions.find(
          (action) => action.permission === readPermission,
        )
      : undefined;
    if (policy.enabled && readAction && !draft.actions[readAction.id]?.enabled)
      throw new Error('编辑、管理或导出动作必须以前置查看动作为基础');
  }

  const fieldRows = new Map(
    buildPermissionBusinessFieldRows(state.catalog.quoteFieldDefinitions).map(
      (row) => [row.id, row],
    ),
  );
  const fieldGrantIds = Object.keys(draft.fieldGrants);
  if (
    fieldGrantIds.length !== fieldRows.size ||
    fieldGrantIds.some((id) => !fieldRows.has(id))
  )
    throw new Error('字段权限草稿包含未知或缺失项');
  for (const [id, grant] of Object.entries(draft.fieldGrants)) {
    const row = fieldRows.get(id);
    if (
      !row ||
      typeof grant.view !== 'boolean' ||
      typeof grant.edit !== 'boolean' ||
      typeof grant.export !== 'boolean' ||
      ((!row.available || !row.editable) && grant.edit) ||
      (!row.available && (grant.view || grant.export)) ||
      ((grant.edit || grant.export) && !grant.view)
    )
      throw new Error('字段权限越界');
  }

  const approvedChannels = new Map(
    approved.channels.map((channel) => [channel.id, channel]),
  );
  const channelIds = Object.keys(draft.channels);
  if (
    channelIds.length !== approvedChannels.size ||
    channelIds.some((id) => !approvedChannels.has(id))
  )
    throw new Error('通道草稿包含未知或缺失项');
  for (const [id, policy] of Object.entries(draft.channels)) {
    const channel = approvedChannels.get(id);
    const requiredGrant = channelRequiredFieldGrant(id);
    if (
      !channel ||
      typeof policy.enabled !== 'boolean' ||
      (policy.enabled && channel.decision !== 'allow') ||
      !Array.isArray(policy.fieldIds) ||
      new Set(policy.fieldIds).size !== policy.fieldIds.length ||
      !requiredGrant ||
      policy.fieldIds.some((fieldId) => {
        const row = fieldRows.get(fieldId);
        const grant = draft.fieldGrants[fieldId];
        return (
          !row?.available ||
          !grant?.view ||
          !grant[requiredGrant] ||
          (requiredGrant === 'edit' && !row.editable)
        );
      })
    )
      throw new Error('通道政策越出角色可见字段');
  }

  for (const [userId, adjustment] of Object.entries(draft.memberAdjustments)) {
    const user = state.catalog.users.find((item) => item.id === userId);
    if (
      !user ||
      user.roleId !== roleId ||
      adjustment.userId !== userId ||
      adjustment.roleId !== roleId ||
      new Set(adjustment.deniedPermissionIds).size !==
        adjustment.deniedPermissionIds.length ||
      adjustment.deniedPermissionIds.some(
        (permission) =>
          !approved.features
            .flatMap((feature) => feature.actions)
            .some(
              (action) =>
                action.permission === permission &&
                draft.actions[action.id]?.enabled,
            ),
      )
    )
      throw new Error('成员微调归属或动作无效');
  }

  const next = clone(state);
  next.catalog.permissionGovernance.permissionVersion += 1;
  next.catalog.permissionGovernance.roles[roleId] = {
    ...structuredClone(draft),
    publishedVersion: current.publishedVersion + 1,
  };
  return next;
}

/** Admin-only management of the independent quote display pool.  Definitions
 * and presets are intentionally not mutated by this transition. */
export function saveQuoteFieldPool(
  state: CpqState,
  pool: QuoteFieldPoolRecord[],
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('仅超级管理员可治理报价字段池');
  const definitions = new Map(
    state.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  for (const descriptor of createSystemFieldRegistry()) {
    if (!definitions.has(descriptor.definition.key))
      definitions.set(descriptor.definition.key, descriptor.definition);
  }
  const ids = new Set<string>();
  const keys = new Set<string>();
  const canonicalSystemPool = createQuoteFieldPool();
  const canonicalSystemByKey = new Map(
    canonicalSystemPool.map((entry) => [entry.fieldKey, entry]),
  );
  for (const entry of pool) {
    if (!entry.id.trim() || ids.has(entry.id) || keys.has(entry.fieldKey))
      throw new Error('字段池标识或来源字段重复');
    ids.add(entry.id);
    keys.add(entry.fieldKey);
    const field = definitions.get(entry.fieldKey);
    if (
      !field?.enabled ||
      !isQuoteDefinitionBound(field.key) ||
      field.governance.classification !== 'ordinary' ||
      !isSafeDefinitionVersion(field.governance.definitionVersion) ||
      field.governance.fieldKey !== field.key
    )
      throw new Error('字段池只能引用已启用普通字段');
    if (
      !['quote-line', 'package-summary', 'quote-header'].includes(
        entry.placement,
      ) ||
      entry.snapshotPolicy !== 'freeze-on-select' ||
      typeof entry.enabled !== 'boolean' ||
      !entry.displayLabel.trim() ||
      !Number.isInteger(entry.sortOrder) ||
      entry.sortOrder < 1
    )
      throw new Error('字段池配置无效');
    const canonicalEntry = canonicalSystemByKey.get(entry.fieldKey);
    if (canonicalEntry && !sameJson(entry, canonicalEntry))
      throw new Error('系统字段池成员、名称、落点与默认状态不可修改');
    if (FIXED_QUOTE_HEADER_FIELD_KEYS.includes(entry.fieldKey as never))
      throw new Error('固定报价抬头字段不进入报价字段池');
    const binding = unifiedFieldBindingForKey(
      state.catalog.quoteFieldDefinitions,
      field.key,
    );
    if (!binding?.poolAllowed || binding.quotePlacement !== entry.placement)
      throw new Error('报价字段池落点与字段归属不一致');
  }
  if (
    canonicalSystemPool.some((canonical) => {
      const entry = pool.find(
        (candidate) => candidate.fieldKey === canonical.fieldKey,
      );
      return !entry || !sameJson(entry, canonical);
    }) ||
    pool.some((entry) => {
      const canonical = canonicalSystemByKey.get(entry.fieldKey);
      return canonical !== undefined && !sameJson(entry, canonical);
    })
  ) {
    throw new Error('系统字段池成员、名称、落点与默认状态不可修改');
  }
  const selectablePoolKeys = new Set(
    pool
      .filter(
        (entry) =>
          entry.enabled &&
          entry.placement === 'quote-line' &&
          isQuoteDefinitionBound(entry.fieldKey),
      )
      .map((entry) => entry.fieldKey),
  );
  if (
    state.catalog.quoteFieldCombinations.some(
      (combination) =>
        combination.enabled &&
        combination.fieldKeys.some((key) => !selectablePoolKeys.has(key)),
    )
  ) {
    throw new Error('字段池变更将使已启用快捷组合越界，请先修改组合');
  }
  const candidateState = {
    ...state,
    catalog: { ...state.catalog, quoteFieldPool: pool },
  } satisfies CpqState;
  for (const combination of state.catalog.shareFieldCombinations ?? []) {
    if (!combination.enabled) continue;
    if (
      shareFieldCombinationFieldsOutsidePool(candidateState, combination)
        .length > 0
    )
      throw new Error(
        '字段池变更将使已启用分享字段组合越界，请先修改分享字段组合；设备图片、设备名称不可移出或停用',
      );
  }
  const next = clone(state);
  next.catalog.quoteFieldPool = [...pool]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((entry, index) => ({
      ...structuredClone(entry),
      displayLabel: entry.displayLabel.trim(),
      snapshotPolicy: 'freeze-on-select' as const,
      sortOrder: index + 1,
    }));
  return next;
}

/** Delete only an unused, unpooled custom definition. Existing values and
 * calculation dependencies are facts, so deletion fails closed and never
 * erases them. */
export function deleteUnpooledQuoteField(
  state: CpqState,
  fieldKey: string,
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('当前角色无字段治理权限');
  const field = state.catalog.quoteFieldDefinitions.find(
    (candidate) => candidate.key === fieldKey,
  );
  if (field?.governance.kind !== 'custom')
    throw new Error('只能删除企业自定义字段');
  if (state.catalog.quoteFieldPool.some((entry) => entry.fieldKey === fieldKey))
    throw new Error('已入报价字段池的字段不能删除，请先移出字段池');
  const hasValue =
    [
      ...state.catalog.devices,
      ...state.catalog.packages,
      ...state.catalog.plans,
      ...state.catalog.customers,
    ].some((record) => record.customFieldValues?.[fieldKey] !== undefined) ||
    Object.values(state.quotes).some(
      (quote) =>
        quote.customFieldValues?.[fieldKey] !== undefined ||
        quote.items.some(
          (item) => item.manualFieldValues?.[fieldKey] !== undefined,
        ) ||
        (quote.fieldSelections ?? []).some(
          (selection) => selection.fieldKey === fieldKey,
        ),
    );
  const dependent = state.catalog.quoteFieldDefinitions.some(
    (candidate) =>
      candidate.key !== fieldKey &&
      candidate.calculation?.operands.some(
        (operand) => operand.kind === 'field' && operand.fieldKey === fieldKey,
      ),
  );
  if (hasValue || dependent)
    throw new Error(
      '字段已有值或被计算字段依赖，删除被拒绝；请先停用，历史值不会丢失',
    );
  const next = clone(state);
  next.catalog.quoteFieldDefinitions =
    next.catalog.quoteFieldDefinitions.filter(
      (candidate) => candidate.key !== fieldKey,
    );
  next.catalog.quoteFieldPresets = next.catalog.quoteFieldPresets.map(
    (preset) => ({
      ...preset,
      fieldKeys: preset.fieldKeys.filter((key) => key !== fieldKey),
      fieldOverrides: preset.fieldOverrides
        ? Object.fromEntries(
            Object.entries(preset.fieldOverrides).filter(
              ([key]) => key !== fieldKey,
            ),
          )
        : undefined,
    }),
  );
  for (const policy of Object.values(next.catalog.permissionGovernance.roles))
    delete policy.fieldGrants[`definition:${fieldKey}`];
  return next;
}

export function saveQuoteFieldCombinations(
  state: CpqState,
  combinations: QuoteFieldCombinationRecord[],
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('仅超级管理员可治理全局快捷组合');
  if (combinations.filter((item) => item.isDefault === true).length > 1) {
    throw new Error('只能有一个默认快捷组合');
  }
  const poolKeys = new Set(
    state.catalog.quoteFieldPool
      .filter(
        (entry) =>
          entry.enabled &&
          entry.placement === 'quote-line' &&
          isQuoteDefinitionBound(entry.fieldKey),
      )
      .map((entry) => entry.fieldKey),
  );
  const ids = new Set<string>();
  const currentById = new Map(
    state.catalog.quoteFieldCombinations.map((item) => [item.id, item]),
  );
  const incomingIds = new Set(combinations.map((item) => item.id));
  if (
    state.catalog.quoteFieldCombinations.some(
      (item) => item.systemLocked && !incomingIds.has(item.id),
    )
  ) {
    throw new Error('系统快捷组合不可删除');
  }
  for (const item of combinations) {
    const current = currentById.get(item.id);
    if (
      !item.id.trim() ||
      ids.has(item.id) ||
      !item.name.trim() ||
      typeof item.enabled !== 'boolean' ||
      typeof item.systemLocked !== 'boolean' ||
      (item.scope !== undefined &&
        !['全公司', '本部门', '管理层'].includes(item.scope)) ||
      (item.isDefault !== undefined && typeof item.isDefault !== 'boolean') ||
      (item.isDefault === true && !item.enabled) ||
      !Number.isInteger(item.sortOrder) ||
      item.sortOrder < 1 ||
      !item.fieldKeys.length ||
      new Set(item.fieldKeys).size !== item.fieldKeys.length ||
      item.fieldKeys.some((key) => !poolKeys.has(key)) ||
      (current && current.systemLocked !== item.systemLocked) ||
      (!current && item.systemLocked)
    )
      throw new Error('快捷组合配置无效或越出启用字段池');
    ids.add(item.id);
  }
  const next = clone(state);
  next.catalog.quoteFieldCombinations = [...combinations]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item, index) => ({
      ...structuredClone(item),
      name: item.name.trim(),
      sortOrder: index + 1,
    }));
  return next;
}

/** New global quick-combination writes are stricter than legacy preset
 * compatibility: every selected key must already be in the enabled pool. */
export function saveQuoteFieldPresets(
  state: CpqState,
  presets: QuoteFieldPresetRecord[],
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('仅超级管理员可治理全局快捷组合');
  const poolKeys = new Set(
    state.catalog.quoteFieldPool
      .filter((entry) => entry.enabled && entry.placement === 'quote-line')
      .map((entry) => entry.fieldKey),
  );
  const currentById = new Map(
    state.catalog.quoteFieldPresets.map((preset) => [preset.id, preset]),
  );
  const global = presets.filter(
    (preset) =>
      !preset.departmentId && !sameJson(preset, currentById.get(preset.id)),
  );
  if (
    new Set(global.map((preset) => preset.id)).size !== global.length ||
    global.some(
      (preset) =>
        preset.fieldKeys.length === 0 ||
        preset.fieldKeys.some((key) => !poolKeys.has(key)),
    )
  ) {
    throw new Error('组合只能引用已启用字段池中的普通字段');
  }
  return saveQuoteFieldConfiguration(
    state,
    state.catalog.quoteFieldDefinitions,
    presets,
    { role: 'super_admin' },
  );
}

/** Disposable-only publication seam. It stores metadata, never an executable formula. */
export function publishQuoteFieldFormulaPreview(
  state: CpqState,
  fieldKey: string,
  evidence: DisposableFormulaPublicationEvidence,
): CpqState {
  const formulaVersion = evidence?.formulaVersion;
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('当前角色无公式治理权限；仅超级管理员可治理公式');
  if (!isSafePreviewFormulaVersion(formulaVersion))
    throw new Error('演示公式版本无效');
  const current = state.catalog.quoteFieldDefinitions.find(
    (field) => field.key === fieldKey,
  );
  if (
    current?.governance.kind !== 'system-calculated' ||
    !current.governance.formulaRef ||
    !['pending-3.33', 'published-3.33'].includes(
      current.governance.formulaStatus ?? '',
    ) ||
    quoteDefinitionBindingForKey(fieldKey)?.calculationRef ===
      current.governance.formulaRef
  ) {
    throw new Error('字段未绑定待审批公式');
  }
  const requestedVersion = Number(formulaVersion.slice('preview-v'.length));
  const currentFormulaVersion = current.governance.formulaVersion;
  if (
    currentFormulaVersion &&
    !isSafePreviewFormulaVersion(currentFormulaVersion)
  ) {
    throw new Error('已有演示公式版本无效，拒绝覆盖');
  }
  const currentVersion = currentFormulaVersion
    ? Number(currentFormulaVersion.slice('preview-v'.length))
    : 0;
  if (requestedVersion <= currentVersion) {
    throw new Error('演示公式版本必须严格递增，不能重复或降序发布');
  }
  if (current.governance.definitionVersion !== 'definition-v1') {
    throw new Error('字段定义版本不匹配，拒绝发布演示公式');
  }
  if (
    !consumeDisposablePublicationEvidence(evidence, fieldKey, formulaVersion)
  ) {
    throw new Error('缺少有效且未消费的 Disposable 发布证明');
  }
  const next = clone(state);
  next.catalog.quoteFieldDefinitions = next.catalog.quoteFieldDefinitions.map(
    (field) =>
      field.key !== fieldKey
        ? field
        : {
            ...field,
            governance: {
              ...field.governance,
              formulaStatus: 'published-3.33',
              definitionVersion: 'definition-v1',
              formulaVersion,
            },
          },
  );
  return next;
}
